import { useMemo } from 'react';
import { selectRoles, selectToken } from '../../store/reducers/AuthSlice';
import { useAppSelector } from '../../store/store';
import {
  buildPermissions,
  resolvePermissionOverridesFromToken,
} from './permissions';

/**
 * Single source of truth for "what is this user allowed to do?".
 *
 * Role table (from product spec):
 *
 * | Section                                       | User | ClinicAdmin | Admin |
 * |-----------------------------------------------|:----:|:-----------:|:-----:|
 * | Journals (outgoing/incoming/disp/survey)      |  +   |      +      |   +   |
 * | Manual upload of tasks                        |  -   |      +      |   +   |
 * | Statistics (all 4 sections)                   |  +   |      +      |   +   |
 * | Analytics module (when installed)              |  +   |      +      |   +   |
 * | Profile                                       |  +   |      +      |   +   |
 * | Black list (page + add/remove)                |  -   |      +      |   +   |
 * | SIP accounts                                  |  -   |      -      |   +   |
 * | Settings → Users                              |  -   |      -      |   +   |
 * | Settings → Calls                              |  -   |      -      |   +   |
 * | Settings → Call pools                         |  -   |      -      |   -   |
 * | Survey templates                              |  -   |      +      |   +   |
 *
 * Each capability boolean is derived from the user's roles. Build new UI gates
 * on top of these flags rather than reading roles directly — that keeps the
 * matrix above as the single, auditable source of truth.
 */
export interface Permissions {
  // Page access
  canViewJournals: boolean;
  canViewStatistics: boolean;
  canViewAnalytics: boolean;
  canViewProfile: boolean;
  canViewBlacklist: boolean;
  canViewSipAccounts: boolean;
  canViewUsers: boolean;
  canViewCallSettings: boolean;
  canViewCallPools: boolean;
  canViewSurveyTemplates: boolean;
  canViewSettingsMenu: boolean;

  // Per-feature gates
  canUploadTasks: boolean;
  canManageSurveyTemplates: boolean;
  canManageBlacklist: boolean;
  canAssignAdministratorRole: boolean;
}

export const usePermissions = (): Permissions => {
  const roles = useAppSelector(selectRoles) as string[];
  const token = useAppSelector(selectToken);

  return useMemo<Permissions>(
    () =>
      buildPermissions(roles ?? [], resolvePermissionOverridesFromToken(token)),
    [roles, token],
  );
};
